import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  MoreVertical, 
  Edit2, 
  Trash2, 
  Mail, 
  Phone,
  ArrowRight
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", contact: "" });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error("Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Client name is required");
      return;
    }

    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, formData);
        toast.success("Client updated successfully");
      } else {
        await axios.post(`${API}/clients`, formData);
        toast.success("Client created successfully");
      }
      setShowModal(false);
      setEditingClient(null);
      setFormData({ name: "", email: "", contact: "" });
      fetchClients();
    } catch (error) {
      toast.error(editingClient ? "Failed to update client" : "Failed to create client");
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email || "",
      contact: client.contact || ""
    });
    setShowModal(true);
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm("Are you sure? This will delete all orders for this client.")) {
      return;
    }
    try {
      await axios.delete(`${API}/clients/${clientId}`);
      toast.success("Client deleted successfully");
      fetchClients();
    } catch (error) {
      toast.error("Failed to delete client");
    }
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (client.email && client.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Clients
          </h1>
          <p className="text-gray-500 mt-1">
            Manage your clients and their orders
          </p>
        </div>
        <button
          onClick={() => {
            setEditingClient(null);
            setFormData({ name: "", email: "", contact: "" });
            setShowModal(true);
          }}
          className="btn-primary flex items-center gap-2"
          data-testid="add-client-btn"
        >
          <Plus size={18} />
          Add Client
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Search clients..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input-field pl-10"
          data-testid="search-clients"
        />
      </div>

      {/* Clients Grid */}
      {filteredClients.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
          {filteredClients.map((client) => (
            <div
              key={client.id}
              className="stat-card group"
              data-testid={`client-card-${client.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                    {client.name}
                  </h3>
                  {client.email && (
                    <div className="flex items-center gap-2 mt-2 text-gray-500">
                      <Mail size={14} />
                      <span className="text-sm truncate">{client.email}</span>
                    </div>
                  )}
                  {client.contact && (
                    <div className="flex items-center gap-2 mt-1 text-gray-500">
                      <Phone size={14} />
                      <span className="text-sm">{client.contact}</span>
                    </div>
                  )}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className="btn-ghost p-1 opacity-0 group-hover:opacity-100"
                      data-testid={`client-menu-${client.id}`}
                    >
                      <MoreVertical size={18} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem 
                      onClick={() => handleEdit(client)}
                      className="cursor-pointer"
                      data-testid={`edit-client-${client.id}`}
                    >
                      <Edit2 size={14} className="mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleDelete(client.id)}
                      className="text-red-600 focus:text-red-600 cursor-pointer"
                      data-testid={`delete-client-${client.id}`}
                    >
                      <Trash2 size={14} className="mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <Link
                to={`/clients/${client.id}`}
                className="mt-4 flex items-center justify-between p-3 -mx-3 -mb-3 rounded-b-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                data-testid={`view-client-${client.id}`}
              >
                <span className="text-sm text-gray-600">View Orders</span>
                <ArrowRight size={16} className="text-gray-400" />
              </Link>
            </div>
          ))}
        </div>
      ) : (
        <div className="stat-card text-center py-12" data-testid="no-clients">
          <p className="text-gray-500">
            {searchQuery ? "No clients found matching your search." : "No clients yet. Add your first client to get started."}
          </p>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent data-testid="client-modal">
          <DialogHeader>
            <DialogTitle>
              {editingClient ? "Edit Client" : "Add New Client"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Client Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter client name"
                data-testid="client-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email address"
                data-testid="client-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact">Contact Number</Label>
              <Input
                id="contact"
                value={formData.contact}
                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                placeholder="Enter contact number"
                data-testid="client-contact-input"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                data-testid="cancel-client-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="save-client-btn"
              >
                {editingClient ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
